import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Action } from './Action'
import { MenuTrigger } from './AnchoredMenu'
import type { MenuItem } from './AnchoredMenu'

/**
 * A row of actions that folds into a menu only when it runs out of room.
 *
 * The alternative was a breakpoint, which is what the rest of the app uses to
 * answer "does this fit" — and it is the wrong tool here, because the answer
 * depends on the actions rather than on the window. A project with money
 * visible carries one more control than one without; "Unarchive" is half the
 * width of "Archive project"; the record draws this in a full-bleed sheet on
 * one screen and in the page header on another. A single width cannot be right
 * for all of those, and a width chosen for the widest case would collapse the
 * others long before they needed it.
 *
 * So it measures. A hidden copy of the expanded row sits in the same box and
 * reports what the row *would* take; a ResizeObserver reports what there is.
 * Collapse when the first exceeds the second. The copy is why this does not
 * flicker and does not oscillate: the decision is made against a constant
 * (what the full row wants) rather than against the rendered result, so
 * collapsing can never change the measurement that caused it.
 *
 * `always` never folds — the one or two things this row is *for*. `collapsible`
 * are the rest, in the order they should appear both as buttons and as menu
 * items, so the two readings cannot drift.
 *
 * One caveat, and it is the price of measuring: everything passed here is
 * rendered twice, once for real and once into the ruler. Buttons are fine —
 * they are what this is for. Anything that holds state, runs an effect, or
 * counts its own renders is not, because there would be two of it.
 */

/**
 * Extends MenuItem rather than restating it, so everything a menu row can say
 * an action can say — `danger` in particular, which colours the menu row and
 * the inline chip from the one declaration and is why Delete cannot end up
 * looking like Archive in one reading and not the other.
 */
export interface OverflowAction extends Omit<MenuItem, 'value'> {
  /** Distinguishes the items; also what comes back internally on pick. */
  value: string
  onPick: () => void
}

interface OverflowActionsProps {
  always?: ReactNode
  collapsible: OverflowAction[]
  /** The menu's title row, when it has folded. */
  menuTitle?: string
  /** The collapsed trigger's label. */
  menuLabel?: string
  style?: CSSProperties
}

export function OverflowActions({
  always,
  collapsible,
  menuTitle,
  menuLabel = '⋯',
  style,
}: OverflowActionsProps) {
  const box = useRef<HTMLDivElement>(null)
  const ruler = useRef<HTMLDivElement>(null)
  const [collapsed, setCollapsed] = useState(false)

  // Layout effect, not effect: measuring after paint means one frame with the
  // wrong row on screen, and this sits in a header where that reads as a flash.
  useLayoutEffect(() => {
    const outer = box.current
    const inner = ruler.current
    if (!outer || !inner) return

    const measure = () => {
      const room = outer.getBoundingClientRect().width
      // No room reported at all means the row is not laid out yet — a sheet
      // still opening, a display:none ancestor. Measuring that would collapse
      // everything on principle, so leave the last answer standing.
      if (room === 0) return
      setCollapsed(inner.scrollWidth > room + 1)
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [collapsible, always])

  // Fonts land after first paint and every label gets wider, so a row measured
  // against the fallback face can be a few pixels out. Re-measure once they are
  // in rather than tracking every load.
  useEffect(() => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts
    if (!fonts) return

    let live = true
    void fonts.ready.then(() => {
      if (!live) return
      const outer = box.current
      const inner = ruler.current
      if (!outer || !inner) return
      const room = outer.getBoundingClientRect().width
      if (room > 0) setCollapsed(inner.scrollWidth > room + 1)
    })
    return () => {
      live = false
    }
  }, [])

  // The tone travels with the item, so an action reads the same whichever way
  // it is drawn. Without this Delete arrived as a muted chip inline and a plain
  // row in the menu — indistinguishable from Archive beside it, which is the
  // one thing a destructive action must never be.
  const buttons = collapsible.map((one) => (
    <Action
      key={one.value}
      size="md"
      quietTone={one.danger ? 'danger' : 'muted'}
      onClick={one.onPick}
    >
      {one.label}
    </Action>
  ))

  return (
    <div
      ref={box}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: 1,
        justifyContent: 'flex-end',
        // `minWidth: 0` is what lets this be squeezed until it decides to
        // fold. Once it *has* folded there is nothing left to give — `always`
        // actions never collapse, and the trigger cannot get smaller — so it
        // stops pretending: it refuses to shrink below what it is still
        // showing, the row it sits in overflows honestly, and a parent that
        // can wrap will wrap.
        //
        // Without this it kept reporting whatever width it was handed while
        // drawing a 125px row inside 45px of it — and because the content is
        // right-aligned, the overflow went *leftwards*, straight over the
        // title beside it (found on the client record at 768px with a long
        // name, 2026-09-14).
        minWidth: collapsed ? 'max-content' : 0,
        ...style,
      }}
    >
      {always}

      {collapsed ? (
        <MenuTrigger
          label={menuLabel}
          style={{ font: '600 15px Manrope,sans-serif', color: 'var(--color-muted)', padding: '0 4px' }}
          menu={{
            title: menuTitle,
            items: collapsible.map(({ onPick: _onPick, ...item }) => item),
            onPick: (value) => collapsible.find((one) => one.value === value)?.onPick(),
          }}
        />
      ) : (
        buttons
      )}

      {/* What the expanded row wants, measured where it would actually sit so
          it inherits the same font and spacing. Out of the flow, out of the
          accessibility tree, and never focusable — a second copy of every
          button is exactly the kind of thing that ends up in a tab order. */}
      <div
        ref={ruler}
        aria-hidden="true"
        inert
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          whiteSpace: 'nowrap',
        }}
      >
        {always}
        {buttons}
      </div>
    </div>
  )
}
